<template>
  <v-card id="record-mitigation-dialog">
    <v-card-title class="primary white--text">
      <v-icon color="tertiary" class="mr-2">mdi-clipboard-check-outline</v-icon>
      <span class="headline">{{ title }}</span>
      <v-spacer/>
      <BaseDialogActions hideRefresh hideFullscreen customCancel @close="$emit('cancel')"/>
    </v-card-title>

    <v-card-text class="pt-5">
      <v-row no-gutters>
      <v-col cols="12" :lg="hasMapCoordinates ? 7 : 12" :class="{ 'pr-lg-4': hasMapCoordinates }">
        <!-- Group picker — Manual-mode create only; the group is fixed once saved -->
        <div v-if="showGroupPicker" class="mb-5">
          <div class="text-subtitle-2 mb-1">{{ $t('selectMitigationGroup') }}</div>
          <p class="text-caption grey--text text--darken-1 mb-2">{{ $t('selectMitigationGroupHelp') }}</p>
          <v-select
          v-model="selectedGroupId"
          :items="availableGroups"
          item-value="mitigationGroupId"
          item-text="name"
          :placeholder="$t('selectPlaceholder')"
          :menu-props="{ maxWidth: menuWidth }"
          outlined
          dense
          hide-details
          @focus="measureMenuWidth">
            <template #selection="{ item: groupOption }">
              <v-icon :color="groupOption.color" small class="mr-2">mdi-square</v-icon>
              {{ groupOption.name }}
            </template>
            <template #item="{ item: groupOption }">
              <v-list-item-content>
                <v-list-item-title class="d-flex align-center">
                  <v-icon :color="groupOption.color" small class="mr-2">mdi-square</v-icon>
                  {{ groupOption.name }}
                </v-list-item-title>
                <div
                v-if="expandedGroupDescriptionId === groupOption.mitigationGroupId"
                class="mt-1"
                style="white-space: normal;">
                  <div class="dd-eyebrow">{{ $t('groupDescription') }}</div>
                  <div class="text-caption grey--text text--darken-2">{{ groupOption.description || $t('noGroupDescription') }}</div>
                </div>
              </v-list-item-content>
              <v-list-item-action class="my-0">
                <v-btn icon small @click.stop="toggleGroupDescription(groupOption.mitigationGroupId)">
                  <v-icon color="info" small>{{ expandedGroupDescriptionId === groupOption.mitigationGroupId ? 'mdi-information' : 'mdi-information-outline' }}</v-icon>
                </v-btn>
              </v-list-item-action>
            </template>
          </v-select>
        </div>

        <div v-if="currentGroup && !showGroupPicker" class="d-flex align-center pa-3 mb-5 rounded" style="background: rgba(0,0,0,0.04);">
          <v-icon :color="currentGroup.color" class="mr-2" small>mdi-square</v-icon>
          <span class="text-subtitle-2 mr-3">{{ currentGroup.name }}</span>
          <span class="text-body-2 grey--text text--darken-1">{{ speciesSummary }}</span>
        </div>

        <div v-if="speciesEditable && !readOnly && currentGroup && !currentGroup.requiresHabitat" class="mb-5">
          <div class="text-subtitle-2 mb-1">{{ $t('speciesInThisGroup') }}</div>
          <p class="text-caption grey--text text--darken-1 mb-0">{{ $t('groupDoesNotTrackSpeciesHelp') }}</p>
        </div>

        <div v-else-if="speciesAllowed && !readOnly" class="mb-5">
          <div class="text-subtitle-2 mb-1">{{ $t('speciesInThisGroup') }}</div>
          <p class="text-caption grey--text text--darken-1 mb-2">{{ $t('editSpeciesHelp') }}</p>
          <v-card v-if="editableSpecies.length" outlined class="px-3 pt-8 pb-2 mb-2">
            <v-row
            v-for="(sp, index) in editableSpecies"
            :key="sp.uid"
            dense
            class="align-center my-n4">
              <v-col cols="12" sm="5">
                <v-text-field
                v-model="sp.commonName"
                :label="$t('commonName')"
                outlined
                dense
                :maxlength="maxNameLength"
                :error-messages="speciesRowError(sp)"/>
              </v-col>
              <v-col cols="12" sm="5">
                <v-text-field
                v-model="sp.scientificName"
                :label="$t('scientificName')"
                outlined
                dense
                :maxlength="maxNameLength"/>
              </v-col>
              <v-col cols="12" sm="2" class="d-flex justify-end pb-7">
                <v-tooltip bottom :color="canCheckNatureServe(sp) ? 'primary' : 'grey'">
                  <template #activator="{ on }">
                    <span v-on="on">
                      <v-btn
                      icon
                      small
                      color="primary"
                      :disabled="!canCheckNatureServe(sp)"
                      @click="checkNatureServe(sp)">
                        <v-icon small>mdi-database-search-outline</v-icon>
                      </v-btn>
                    </span>
                  </template>
                  <span class="white--text text-subtitle-2">{{ canCheckNatureServe(sp) ? $t('checkNatureServe') : $t('natureServeNameRequired') }}</span>
                </v-tooltip>
                <v-btn icon small color="error" :title="$t('removeSpecies')" @click="removeSpecies(index)">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-card>
          <v-btn small text color="primary" @click="addSpecies">
            <v-icon small left>mdi-plus</v-icon>
            {{ $t('addSpecies') }}
          </v-btn>
        </div>

        <div v-else-if="speciesHits.length" class="mb-5">
          <div class="text-subtitle-2 mb-1">{{ $t('speciesInThisGroup') }}</div>
          <p class="text-caption grey--text text--darken-1 mb-2">{{ $t('speciesInThisGroupHelp') }}</p>
          <v-card outlined rounded>
            <div
            v-for="(sp, index) in speciesHits"
            :key="index"
            class="d-flex align-center justify-space-between px-3 py-2"
            :class="{ 'dd-species-divider': index > 0 }">
              <span>
                <span class="text-body-2 font-weight-medium">{{ sp.commonName }}</span>
                <span v-if="sp.scientificName" class="text-caption font-italic grey--text text--darken-1 ml-2">{{ sp.scientificName }}</span>
              </span>
              <v-btn v-if="canCheckNatureServe(sp)" small outlined color="primary" @click="$emit('check-nature-serve', { common: sp.commonName, scientific: sp.scientificName })">
                <v-icon small left>mdi-database-search-outline</v-icon>
                {{ $t('checkNatureServe') }}
              </v-btn>
            </div>
          </v-card>
        </div>

        <div v-if="currentGroup && currentGroup.requiresHabitat" class="mb-5">
          <div class="text-subtitle-2 mb-1">{{ $t('habitatNames') }}</div>
          <p class="text-caption grey--text text--darken-1 mb-2">{{ $t('habitatNamesHelp') }}</p>
          <HabitatAutocomplete
          v-model="selectedHabitatIds"
          :habitats="habitats"
          :readOnly="readOnly"
          showDescriptions/>
        </div>

        <div v-if="currentGroup" class="mb-5">
          <div class="text-subtitle-2 mb-1">{{ presenceLabel }}</div>
          <p class="text-caption grey--text text--darken-1 mb-2">{{ presenceHelp }}</p>
          <v-select
          v-model="presenceChoice"
          :items="presenceOptions"
          item-text="text"
          item-value="value"
          outlined
          dense
          hide-details
          :readonly="readOnly"
          :append-icon="readOnly ? '' : undefined"
          style="max-width: 240px;"/>
        </div>

        <div v-if="showStepsAccordion" class="mb-5">
          <div class="text-subtitle-2 mb-1">{{ $t('mitigationSteps') }}</div>
          <p class="text-caption grey--text text--darken-1 mb-2">{{ $t('mitigationStepsHelp') }}</p>
          <v-expansion-panels multiple accordion popout class="narrow-expansion-panels">
            <v-expansion-panel v-for="habitat in selectedHabitats" :key="habitat.mitigationHabitatId">
              <v-expansion-panel-header>
                <div class="d-flex align-center">
                  <v-icon color="primary" small class="mr-2">mdi-pine-tree</v-icon>
                  <span class="subtitle-2">{{ habitat.name }}</span>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div class="d-flex align-start pa-3">
                  <div class="text-caption grey--text text--darken-2 flex-grow-1" style="white-space: pre-line;">{{ habitat.mitigationSteps || $t('noHabitatSteps') }}</div>
                  <v-tooltip v-if="canAppendSteps(habitat)" bottom color="primary">
                    <template #activator="{ on }">
                      <v-btn
                      small
                      outlined
                      color="primary"
                      class="ml-3 flex-shrink-0"
                      v-on="on"
                      @click="appendSteps(habitat)">
                        <v-icon small left>mdi-text-box-plus-outline</v-icon>
                        {{ $t('appendToComments') }}
                      </v-btn>
                    </template>
                    <span class="white--text text-subtitle-2">{{ $t('appendToCommentsTooltip') }}</span>
                  </v-tooltip>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>

        <div v-if="showStepsReference" class="mb-5">
          <div class="text-subtitle-2 mb-1">
            {{ $t('mitigationSteps') }}
            <span class="font-weight-regular grey--text">· {{ $t('mitigationStepsReference') }}</span>
          </div>
          <p class="text-caption grey--text text--darken-1 mb-2">{{ $t('mitigationStepsReferenceHelp') }}</p>
          <v-textarea
          :value="currentGroup.mitigationSteps || ''"
          :placeholder="$t('noGroupSteps')"
          rows="4"
          outlined
          readonly
          hide-details
          no-resize
          class="ds-reference-textarea"/>
        </div>

        <div v-if="isPresent" class="mb-2">
          <div class="text-subtitle-2 mb-1">{{ $t('riskMitigationComments') }}</div>
          <p class="text-caption grey--text text--darken-1 mb-2">{{ $t('riskMitigationCommentsHelp') }}</p>
          <v-textarea
          v-model="comment"
          :placeholder="$t('riskMitigationCommentsPlaceholder')"
          rows="5"
          outlined
          :readonly="readOnly"
          :counter="readOnly ? false : maxCommentLength"
          :maxlength="maxCommentLength"/>
        </div>
      </v-col>

      <v-col v-if="hasMapCoordinates" cols="12" lg="5" class="rmd-map-col mt-6 mt-lg-0">
        <DueDiligenceMapPanel
        :key="tractId"
        :tractId="tractId"
        :latitude="latitude"
        :longitude="longitude"/>
      </v-col>
      </v-row>
    </v-card-text>

    <v-card-actions class="px-6 pb-4">
      <template v-if="readOnly">
        <v-spacer/>
        <v-btn text outlined color="black" @click="$emit('cancel')">{{ $t('close') }}</v-btn>
      </template>
      <template v-else>
        <v-btn text outlined color="black" @click="$emit('cancel')">{{ $t('cancel') }}</v-btn>
        <v-spacer/>
        <v-btn color="success" :disabled="!canSave" @click="save">
          <v-icon left>mdi-content-save</v-icon>
          {{ $t('saveAndClose') }}
        </v-btn>
      </template>
    </v-card-actions>
  </v-card>
</template>

<script>
import { StringConstants } from '@/utils/constants'
import { RiskPresence } from '@/utils/Enumerations.js'
import { menuWidthFromEvent } from '@/utils/menuWidth.js'
import { canCheckNatureServe, presenceLabel, presenceHelp, speciesCountLabel, backfillSpeciesNames } from '@/utils/dueDiligence/presentation.js'
import { normalizeSpeciesHit, speciesHitKey, duplicateSpeciesHitKeys } from '@/utils/dueDiligence/speciesHits.js'

export default {
  name: 'RecordMitigationDialog',

  components: {
    HabitatAutocomplete: () => import('@/components/due-diligence/HabitatAutocomplete.vue'),
    DueDiligenceMapPanel: () => import('@/components/due-diligence/DueDiligenceMapPanel.vue'),
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue')
  },

  props: {
    item: {
      type: Object,
      required: true
    },
    group: {
      type: Object,
      default: null
    },
    availableGroups: {
      type: Array,
      default: () => []
    },
    // Manual mode only — in SpeciesLayer mode hits are sync-owned and the payload must omit them
    speciesEditable: {
      type: Boolean,
      default: false
    },
    habitats: {
      type: Array,
      required: true
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    // Resolved { commonName, scientificName } from the NatureServe slide-over; back-fills the
    // empty name field(s) of whichever editable species row triggered the lookup.
    natureServeResult: {
      type: Object,
      default: null
    },
    tractId: {
      type: Number,
      default: null
    },
    latitude: {
      type: Number,
      default: null
    },
    longitude: {
      type: Number,
      default: null
    }
  },

  data () {
    return {
      presenceChoice: this.initialPresence(),
      selectedGroupId: this.item.mitigationGroupId ?? null,
      selectedHabitatIds: [...(this.item.mitigationHabitatIds ?? [])],
      comment: this.item.mitigationComment ?? '',
      editableSpecies: (this.item.speciesHits ?? []).map((sp, index) => ({
        uid: index,
        commonName: sp.commonName ?? '',
        scientificName: sp.scientificName ?? ''
      })),
      nextSpeciesUid: (this.item.speciesHits ?? []).length,
      pendingNatureServeUid: null,
      expandedGroupDescriptionId: null,
      menuWidth: undefined,
      maxCommentLength: StringConstants.ExtraExtraExtraLongStringLength,
      maxNameLength: StringConstants.ExtraLongStringLength
    }
  },

  watch: {
    selectedGroupId () {
      this.selectedHabitatIds = []
      if (!this.speciesAllowed) this.editableSpecies = []
    },

    natureServeResult (result) {
      if (!result || this.pendingNatureServeUid == null) return
      const sp = this.editableSpecies.find(s => s.uid === this.pendingNatureServeUid)
      this.pendingNatureServeUid = null
      backfillSpeciesNames(sp, result)
    }
  },

  computed: {
    editing () {
      return this.item.tractMitigationItemId != null
    },

    currentGroup () {
      return this.group ?? this.availableGroups.find(g => g.mitigationGroupId === this.selectedGroupId) ?? null
    },

    showGroupPicker () {
      return !this.editing && this.group == null
    },

    title () {
      if (this.showGroupPicker) return this.$t('addGroupHit')
      const key = this.readOnly ? 'viewMitigationTitle' : (this.editing ? 'editMitigationTitle' : 'recordMitigationTitle')
      return this.$t(key, { group: this.currentGroup?.name ?? '' })
    },

    speciesHits () {
      return this.item.speciesHits ?? []
    },

    speciesAllowed () {
      return this.speciesEditable && !!this.currentGroup?.requiresHabitat
    },

    speciesSummary () {
      const count = this.speciesEditable && !this.readOnly ? this.editableSpecies.length : this.speciesHits.length
      return count === 0 ? this.$t('noRecordedSpecies') : speciesCountLabel(count)
    },

    presenceLabel () {
      return presenceLabel(this.currentGroup?.requiresHabitat)
    },

    presenceHelp () {
      return presenceHelp(this.currentGroup?.requiresHabitat)
    },

    presenceOptions () {
      return [
        { text: this.$t('selectPlaceholder'), value: RiskPresence.NotSet.value },
        { text: this.$t('present'), value: RiskPresence.Present.value },
        { text: this.$t('notPresent'), value: RiskPresence.NotPresent.value }
      ]
    },

    isPresent () {
      return this.presenceChoice === RiskPresence.Present.value
    },

    selectedHabitats () {
      return this.selectedHabitatIds
        .map(id => this.habitats.find(h => h.mitigationHabitatId === id))
        .filter(Boolean)
        .sort((a, b) => a.name.localeCompare(b.name))
    },

    showStepsAccordion () {
      return !!this.currentGroup?.requiresHabitat && this.isPresent && this.selectedHabitatIds.length > 0
    },

    showStepsReference () {
      return this.currentGroup != null && !this.currentGroup.requiresHabitat && this.isPresent
    },

    duplicateSpeciesKeys () {
      return duplicateSpeciesHitKeys(this.editableSpecies)
    },

    speciesValid () {
      return this.editableSpecies.every(sp => !!(sp.commonName.trim() || sp.scientificName.trim())) &&
        this.duplicateSpeciesKeys.size === 0
    },

    canSave () {
      if (!this.currentGroup) return false
      if (this.speciesAllowed && !this.speciesValid) return false
      if (this.presenceChoice === RiskPresence.NotSet.value) return false
      if (this.isPresent && !this.comment.trim()) return false
      if (this.currentGroup.requiresHabitat && this.isPresent && this.selectedHabitatIds.length === 0) return false
      return true
    },

    hasMapCoordinates () {
      return this.tractId != null && this.latitude != null && this.longitude != null
    }
  },

  methods: {
    canCheckNatureServe,

    checkNatureServe (sp) {
      this.pendingNatureServeUid = sp.uid
      this.$emit('check-nature-serve', { common: sp.commonName, scientific: sp.scientificName })
    },

    initialPresence () {
      return this.item.riskPresence ?? RiskPresence.NotSet.value
    },

    toggleGroupDescription (id) {
      this.expandedGroupDescriptionId = this.expandedGroupDescriptionId === id ? null : id
    },

    measureMenuWidth (event) {
      this.menuWidth = menuWidthFromEvent(event)
    },

    addSpecies () {
      this.editableSpecies.push({ uid: this.nextSpeciesUid++, commonName: '', scientificName: '' })
    },

    removeSpecies (index) {
      this.editableSpecies.splice(index, 1)
    },

    speciesRowError (sp) {
      if (!sp.commonName.trim() && !sp.scientificName.trim()) return [this.$t('speciesNameRequired')]
      return this.duplicateSpeciesKeys.has(speciesHitKey(sp)) ? [this.$t('duplicateSpeciesInGroup')] : []
    },

    canAppendSteps (habitat) {
      return !this.readOnly && !!(habitat.mitigationSteps ?? '').trim()
    },

    appendSteps (habitat) {
      const steps = (habitat.mitigationSteps ?? '').trim()
      if (!steps) return
      const appended = this.comment.trim() ? `${this.comment.trimEnd()}\n${steps}` : steps
      if (appended.length > this.maxCommentLength) {
        this.setSnackError(this.$t('appendStepsExceedsLimit', { max: this.maxCommentLength }))
        return
      }
      this.comment = appended
      this.setSnack(this.$t('stepsAppendedToComments'))
    },

    save () {
      if (!this.canSave) return
      const present = this.isPresent
      const payload = {
        tractMitigationItemId: this.item.tractMitigationItemId,
        mitigationGroupId: this.currentGroup.mitigationGroupId,
        riskPresence: this.presenceChoice,
        mitigationHabitatIds: this.currentGroup.requiresHabitat ? [...this.selectedHabitatIds] : [],
        mitigationComment: present ? this.comment.trim() : ''
      }

      if (this.speciesEditable) {
        const speciesHits = this.speciesAllowed
          ? this.editableSpecies.map(normalizeSpeciesHit)
          : []
        if (this.speciesHitsChanged(speciesHits)) payload.speciesHits = speciesHits
      }
      this.$emit('save', payload)
    },

    speciesHitsChanged (speciesHits) {
      const nameKeys = hits => hits
        .map(sp => JSON.stringify([sp.commonName?.trim() ?? '', sp.scientificName?.trim() ?? '']))
        .sort()
      const stored = nameKeys(this.speciesHits)
      const next = nameKeys(speciesHits)
      return stored.length !== next.length || stored.some((key, index) => key !== next[index])
    }
  }
}
</script>

<style scoped>
.rmd-map-col {
  position: sticky;
  top: 0;
  align-self: flex-start;
}
</style>
